import React, { useMemo } from 'react'
import Skeleton from 'react-loading-skeleton'

import { ChevronRightIcon, PrepTimeIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import isToday from 'date-fns/isToday'
import isTomorrow from 'date-fns/isTomorrow'

import {
  DiningOptionBehavior,
  FulfillmentType
} from 'src/apollo/onlineOrdering'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { getMinimumFulfillmentTime } from 'shared/js/timeUtils'

import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { getQuoteTime } from 'public/components/online_ordering/fulfillmentUtils'

import { t } from 'src/translations'
import { getLocale } from '@toasttab/buffet-pui-locale-utilities'

import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

const getNotYetScheduledFulfillmentTimeString = (
  scheduledOnly: boolean,
  asapOnly: boolean,
  selectedDiningOptionBehavior: DiningOptionBehavior | undefined,
  fulfillmentType: FulfillmentType | undefined,
  minTime: number | null
) => {
  if (scheduledOnly) {
    return t('fulfillment-time-summary.schedule')
  }
  const asapString = asapOnly
    ? t('fulfillment-time-summary.asap-only')
    : t('fulfillment-time-summary.asap')

  if (!minTime) {
    return asapString
  }
  const time = getScheduledFulfillmentTimeString(
    selectedDiningOptionBehavior,
    getMinimumFulfillmentTime(minTime),
    fulfillmentType
  )
  return (
    <>
      <span>{asapString}</span>
      <span>•</span>
      <span>{time}</span>
    </>
  )
}

const getScheduledFulfillmentTimeString = (
  selectedDiningOptionBehavior: DiningOptionBehavior | undefined,
  dateTime: Date,
  fulfillmentType: FulfillmentType | undefined
) => {
  const minutesIntoTheFuture = Math.round(
    (dateTime.getTime() - new Date().getTime()) / (1000 * 60)
  )
  const itIsTomorrow: boolean = isTomorrow(dateTime)
  const itIsToday = isToday(dateTime)
  const isDelivery =
    selectedDiningOptionBehavior === DiningOptionBehavior.Delivery
  const useMinutesRange =
    fulfillmentType === FulfillmentType.Asap &&
    minutesIntoTheFuture <= 60 &&
    isToday(dateTime)

  const locale = getLocale()
  const localizedTime = dateTime.toLocaleTimeString(locale, {
    timeStyle: 'short'
  })
  const localizedDate = dateTime.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric'
  })

  if (isDelivery) {
    if (useMinutesRange) {
      return t('fulfillment-time-summary.estimated-today', {
        minutesIntoTheFuture1: minutesIntoTheFuture,
        minutesIntoTheFuture2: minutesIntoTheFuture + 5
      })
    } else {
      if (itIsToday) {
        return t('fulfillment-time-summary.estimated-today-at', {
          time: localizedTime
        })
      } else if (itIsTomorrow) {
        return t('fulfillment-time-summary.estimated-tomorrow-at', {
          time: localizedTime
        })
      } else {
        return t('fulfillment-time-summary.estimated-date-at', {
          date: localizedDate,
          time: localizedTime
        })
      }
    }
  } else {
    if (useMinutesRange) {
      return t('fulfillment-time-summary.pickup-today', {
        minutesIntoTheFuture1: minutesIntoTheFuture,
        minutesIntoTheFuture2: minutesIntoTheFuture + 5
      })
    } else {
      if (itIsToday) {
        return t('fulfillment-time-summary.pickup-today-at', {
          time: localizedTime
        })
      } else if (itIsTomorrow) {
        return t('fulfillment-time-summary.pickup-tomorrow-at', {
          time: localizedTime
        })
      } else {
        return t('fulfillment-time-summary.pickup-date-at', {
          date: localizedDate,
          time: localizedTime
        })
      }
    }
  }
}

const FulfillmentTimeSummary = ({
  showIcon = true,
  subtitleText
}: {
  showIcon?: boolean
  subtitleText?: string
}) => {
  const {
    diningOptionBehaviorAvailability,
    fulfillmentData,
    guestSelectedFulfillmentTime,
    loadingFulfillment,
    setFulfillmentTimeModalOpen
  } = useFulfillment()
  const { ooRestaurant, orderingDisabled } = useRestaurant()
  const { cart, loadingCart } = useCart()
  const diningOptionBehavior = useMemo(
    () => fulfillmentData?.cartFulfillmentData?.diningOptionBehavior,
    [fulfillmentData?.cartFulfillmentData?.diningOptionBehavior]
  )
  const fulfillmentType = useMemo(
    () => fulfillmentData?.cartFulfillmentData?.fulfillmentType,
    [fulfillmentData?.cartFulfillmentData?.fulfillmentType]
  )
  const pickupAvailability = diningOptionBehaviorAvailability.takeout
  const deliveryAvailability = diningOptionBehaviorAvailability.delivery

  const scheduledOnly = useMemo(() => {
    const deliveryScheduledOnly =
      deliveryAvailability?.scheduledAvailable &&
      !deliveryAvailability?.asapAvailable
    const pickupScheduledOnly =
      pickupAvailability?.scheduledAvailable &&
      !pickupAvailability?.asapAvailable
    return Boolean(
      diningOptionBehavior === DiningOptionBehavior.Delivery
        ? deliveryScheduledOnly
        : pickupScheduledOnly
    )
  }, [deliveryAvailability, pickupAvailability, diningOptionBehavior])

  const asapOnly = useMemo(() => {
    const deliveryAsapOnly =
      deliveryAvailability?.asapAvailable &&
      !deliveryAvailability?.scheduledAvailable
    const pickupAsapOnly =
      pickupAvailability?.asapAvailable &&
      !pickupAvailability?.scheduledAvailable
    return Boolean(
      diningOptionBehavior === DiningOptionBehavior.Delivery
        ? deliveryAsapOnly
        : pickupAsapOnly
    )
  }, [deliveryAvailability, pickupAvailability, diningOptionBehavior])

  const scheduledAvailable = useMemo(
    () =>
      diningOptionBehavior === DiningOptionBehavior.Delivery
        ? deliveryAvailability?.scheduledAvailable
        : pickupAvailability?.scheduledAvailable,
    [diningOptionBehavior, deliveryAvailability, pickupAvailability]
  )

  const fulfillmentTime =
    fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime || undefined
  const minimumTime = useMemo(
    () =>
      ooRestaurant
        ? getQuoteTime(
            diningOptionBehavior ?? DiningOptionBehavior.TakeOut,
            ooRestaurant,
            cart
          )
        : null,
    [cart, ooRestaurant, diningOptionBehavior]
  )

  const { track } = useTracker()
  const requiresTimeSelection = scheduledOnly && !guestSelectedFulfillmentTime

  if (orderingDisabled) {
    return null
  }
  if (loadingFulfillment || loadingCart) {
    return (
      <Skeleton
        className='loadingSkeleton'
        containerTestId='fulfillment-time-loading-skeleton'
      />
    )
  }

  return (
    <button
      id='fulfillment-time-summary-button'
      className='fulfillmentTimeSummary'
      data-testid='fulfillment-time-summary'
      onClick={() => {
        track(TrackingEvents.FULFILLMENT_TIME_SUMMARY_CLICKED, {
          diningOption: diningOptionBehavior,
          fulfillmentTimeType: fulfillmentType,
          fulfillmentTimeStatus: requiresTimeSelection
            ? 'requires-selection'
            : 'valid'
        })
        setFulfillmentTimeModalOpen(true)
      }}
      disabled={!scheduledAvailable}
      type='button'
    >
      <div
        className={classnames(
          'content',
          requiresTimeSelection ? 'actionText' : undefined
        )}
      >
        {showIcon && <PrepTimeIcon size='xs' accessibility='decorative' />}
        <div className='textContent'>
          <div
            className='fulfillmentTimeString'
            data-testid='fulfillment-time-string'
          >
            {guestSelectedFulfillmentTime && fulfillmentTime
              ? getScheduledFulfillmentTimeString(
                  diningOptionBehavior,
                  new Date(fulfillmentTime),
                  fulfillmentType
                )
              : getNotYetScheduledFulfillmentTimeString(
                  scheduledOnly,
                  asapOnly,
                  diningOptionBehavior,
                  fulfillmentType,
                  minimumTime
                )}
          </div>
          {subtitleText && <span className='subtitleText'>{subtitleText}</span>}
        </div>
      </div>
      {scheduledAvailable && (
        <ChevronRightIcon
          size='xs'
          accessibility='semantic'
          aria-label={t('fulfillment-time-summary.details')}
        />
      )}
    </button>
  )
}

export default FulfillmentTimeSummary
